import { Text, Flex, Paper, Stack, Title, Button } from "@mantine/core";
import { colors } from "../theme/colors";
import {
  convertUTCToSpecifiedTimezone,
  filterDatesByGivenDayjs,
  getReadableTimeZone,
} from "../utils/helpers";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEarthAmericas } from "@fortawesome/pro-regular-svg-icons";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import FHConfirmButton from "./FHConfirmButton";
import { useAuth } from "../context/AuthContext";
import { NavContext } from "@ionic/react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  STEADY_MD_PROGRAM_GUID,
  createSteadymdConsult,
  scheduleSteadymdConsult,
  updateSteadymdConsultStatus,
} from "../services/steadymdService";
import {
  Consult,
  ConsultCreateUsStateEnum,
  ConsultVisitStatusEnum,
} from "@futurhealth/steadymd-api-client";
import { useIsMobile } from "../hooks/useIsMobile";
import { useLocation } from "react-router";
import { useAmazonInsuranceCheck } from "../hooks/useAmazonInsuranceCheck";
import { useCurrentUser } from "../hooks/react-query/useCurrentUser";
import { usePostHog } from "posthog-js/react";
import {
  APPOINTMENT_SLOTS_VISIBLE,
  USER_SELECTED_APPOINTMENT_SLOT,
  APPOINTMENT_BOOKED_SUCCESS,
  APPOINTMENT_SLOTS_RENDER_FAILED,
} from "../constants/posthogEvents";

dayjs.extend(utc);
dayjs.extend(timezone);

interface AppointmentTimeSelectProps {
  selectedDate: string;
  availability: string[];
  consult: Partial<Consult> | null | undefined;
  currentTz: string;
  cancelPreviousConsult?: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  onSuccess?: () => void;
}

const AppointmentTimeSelect = ({
  selectedDate,
  availability,
  consult,
  currentTz,
  cancelPreviousConsult,
  setIsLoading,
  onSuccess,
}: AppointmentTimeSelectProps) => {
  const navContext = useContext(NavContext);
  const { currentUser, anonymousLogin } = useAuth();
  const { data: userProfile } = useCurrentUser();
  const isMobile = useIsMobile();
  const [confirmIndex, setConfirmIndex] = useState<number>(-1);
  const location = useLocation();
  const urlRoot = location.pathname.split("/")[1];
  const { productData } = useAmazonInsuranceCheck();
  const posthog = usePostHog();
  const visibleTrackedRef = useRef(false);
  const renderFailedTrackedRef = useRef(false);

  const availabileTimesForDate = useMemo(() => {
    try {
      return filterDatesByGivenDayjs(
        availability,
        dayjs(Number(selectedDate)).tz(currentTz),
        currentTz
      );
    } catch (error) {
      // Track render failure if filtering fails
      if (!renderFailedTrackedRef.current) {
        renderFailedTrackedRef.current = true;
        posthog?.capture(APPOINTMENT_SLOTS_RENDER_FAILED, {
          flow_type: cancelPreviousConsult ? "reschedule" : "schedule",
          error:
            error instanceof Error
              ? error.name ||
                (error as { code?: string }).code ||
                "UnknownError"
              : String(error),
          selectedDate,
          availabilityLength: availability?.length ?? 0,
        });
      }
      return [];
    }
  }, [availability, currentTz, selectedDate, posthog, cancelPreviousConsult]);

  // Reset visibleTrackedRef and renderFailedTrackedRef when availability / selectedDate changes
  // This allows events to fire again for new data or different dates
  useEffect(() => {
    visibleTrackedRef.current = false;
    renderFailedTrackedRef.current = false;
  }, [availability, selectedDate]);

  // Track slots visible when they're rendered (dedup per render/date via visibleTrackedRef)
  useEffect(() => {
    const hasSlots =
      Array.isArray(availabileTimesForDate) &&
      availabileTimesForDate.length > 0;
    if (hasSlots && !visibleTrackedRef.current) {
      visibleTrackedRef.current = true;
      const flowType = cancelPreviousConsult ? "reschedule" : "schedule";
      posthog?.capture(APPOINTMENT_SLOTS_VISIBLE, {
        flow_type: flowType,
        selectedDate,
        tz: currentTz,
        slotsCount: availabileTimesForDate.length,
        totalAvailabilityCount: availability?.length ?? 0,
      });
    }
  }, [
    availabileTimesForDate,
    posthog,
    selectedDate,
    currentTz,
    availability,
    cancelPreviousConsult,
  ]);

  // Track render failure if we have availability but no slots rendered
  useEffect(() => {
    if (!availability || availability.length === 0 || !selectedDate) {
      return;
    }

    let filteredSlots: string[] = [];
    try {
      filteredSlots = filterDatesByGivenDayjs(
        availability,
        dayjs(Number(selectedDate)).tz(currentTz),
        currentTz
      );
    } catch {
      // If filtering fails, we can't determine if availability exists for this date
      return;
    }

    const hasAvailabilityForSelectedDate = filteredSlots.length > 0;

    if (
      hasAvailabilityForSelectedDate &&
      (!availabileTimesForDate || availabileTimesForDate.length === 0) &&
      !renderFailedTrackedRef.current
    ) {
      renderFailedTrackedRef.current = true;
      posthog?.capture(APPOINTMENT_SLOTS_RENDER_FAILED, {
        flow_type: cancelPreviousConsult ? "reschedule" : "schedule",
        reason: "availability_exists_but_no_slots_rendered",
        selectedDate,
        availabilityLength: availability.length,
        currentTz,
      });
    }
  }, [
    availability,
    availabileTimesForDate,
    selectedDate,
    posthog,
    currentTz,
    cancelPreviousConsult,
  ]);

  const handleSelectAppt = async (time: string) => {
    // Track slot selected (use capture directly to track each selection, not just the first)
    posthog?.capture(USER_SELECTED_APPOINTMENT_SLOT, {
      flow_type: cancelPreviousConsult ? "reschedule" : "schedule",
      time,
      selectedDate,
      tz: currentTz,
    });

    if (!currentUser) {
      await anonymousLogin();
    } else {
      const token = await currentUser.getIdToken();
      setIsLoading(true);

      const StatusesToCancel: string[] = [
        ConsultVisitStatusEnum.Assigned,
        ConsultVisitStatusEnum.Unassigned,
        ConsultVisitStatusEnum.Scheduled,
        ConsultVisitStatusEnum.WaitingToSchedule,
        // Removed: ConsultVisitStatusEnum.Rejected - rejected consults are already in terminal state
        // and cannot be canceled via API, which causes rescheduling to fail
      ];

      try {
        if (
          cancelPreviousConsult &&
          consult?.guid &&
          StatusesToCancel.indexOf(consult.visitStatus ?? "") !== -1 &&
          consult.visitStatus !== ConsultVisitStatusEnum.Canceled && // Don't try to cancel an already-canceled consult
          consult.visitStatus !== ConsultVisitStatusEnum.Rejected // Skip rejected consults - they're already terminal
        ) {
          await updateSteadymdConsultStatus(
            consult?.guid,
            ConsultVisitStatusEnum.Canceled,
            token
          );
        }

        const isAsync = userProfile?.isAsync ?? false;
        const newConsult = await createSteadymdConsult(
          {
            programGuid: STEADY_MD_PROGRAM_GUID,
            usState: consult?.usState as ConsultCreateUsStateEnum,
            consultType: consult?.consultType,
            reasonForVisit: consult?.reasonForVisit,
            emrUri: consult?.emrUri ?? "",
            metadata: {
              is_prior_auth_required:
                productData?.pharmacy_provider === "amazon",
            },
          },
          token,
          isAsync
        );

        await scheduleSteadymdConsult(
          newConsult.guid as string,
          time,
          dayjs.tz.guess(),
          token
        );

        // Track booking success (use capture directly to track each booking, not just the first)
        posthog?.capture(APPOINTMENT_BOOKED_SUCCESS, {
          flow_type: cancelPreviousConsult ? "reschedule" : "schedule",
          time,
          selectedDate,
          tz: currentTz,
          consultGuid: newConsult.guid,
        });

        onSuccess?.();
      } catch (err) {
        console.error(err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleTzEdit = () => {
    navContext.navigate(`/${urlRoot}/2/${consult?.guid}`, "forward");
    setConfirmIndex(-1);
  };

  return (
    <Paper radius="md" my={"lg"}>
      <Flex justify={isMobile ? "flex-start" : "center"}>
        <Title fz="28px" fw={600} c={colors.black} lh="36px">
          Select a Time
        </Title>
      </Flex>
      <Flex justify={isMobile ? "flex-start" : "center"} mt="lg">
        <Text fz="16px" c={colors.nearBlack} lh="24px">
          {`Available appointments for ${new Date(Number(selectedDate)).toLocaleDateString()}.`}
        </Text>
      </Flex>
      <Flex
        justify={isMobile ? "space-between" : "center"}
        align="center"
        mt="lg"
      >
        <Text fz="16px" c={colors.darkGray}>
          <FontAwesomeIcon
            icon={faEarthAmericas}
            size="xs"
            style={{ marginRight: "7px" }}
          />
          Timezone
        </Text>
        <Button
          rightSection={<FontAwesomeIcon icon={faChevronRight} />}
          variant="transparent"
          c={colors.nearBlack}
          onClick={() => handleTzEdit()}
        >
          {getReadableTimeZone(currentTz)}
        </Button>
      </Flex>
      <Stack justify="center" align="center" gap="lg" mt="lg">
        {availabileTimesForDate?.map((time, i) => (
          <FHConfirmButton
            key={i}
            index={i}
            confirmIndex={confirmIndex}
            setConfirmIndex={setConfirmIndex}
            onConfirm={() => handleSelectAppt(time)}
          >
            {convertUTCToSpecifiedTimezone(time, currentTz)}
          </FHConfirmButton>
        ))}
      </Stack>
    </Paper>
  );
};

export default AppointmentTimeSelect;
